import { MORPH_HELPER_SCRIPT_ID, MORPH_HELPER_SOURCE } from './gsapMorphHelpers';
import { compositionAgentSource } from './compositionAgent';
import { SNAPDOM_SOURCE } from './snapdomInlineSource.generated';
import {
    GSAP_CDN_URL,
    GSAP_PLUGINS,
    GSAP_SCRIPT_ID,
    gsapPluginScriptId,
    gsapPluginUrl,
} from './htmlManagement';

export const GSAP_SRI: Record<string, string> = {
    gsap: 'sha512-NcZdtrT77bJr4STcmsGAESr06BYGE8woZdSdEgqnpyqac7sugNO+Tr4bGwGF3MsnEkGKhU2KL2xh6Ec+BqsaHA==',
    CustomEase:
        'sha512-CuvnoNrIlhT8htAhjvvnA7EB4sUHfRvfFsjq+JvAKPotN8LlelK/lQgX+u9Gxxsl0+lXmEWRMLPv/oKT1W3dPw==',
    CustomBounce:
        'sha512-PJRGw5+wyjY5yrbQnhzzo0xE9NSx71YD6LPdm9TqjMhR2+ak4Id5exTqt0dcjkiz7LeReq6O4Wkyub12BJ8h4w==',
    CustomWiggle:
        'sha512-97eCqVDWGUZQxIgfw/FL8LA6NKmSOb+3sgRWne8yxDVbov6lGA4B822RKk0a8oaQbAubIhm5t20uq5hnWzDt9w==',
    DrawSVGPlugin:
        'sha512-yOZoZc/D5ZPHwjBduLkgeGHhFlQ7CsslA5D1egpQgeV1QcV4OdCcwmRLkH7p/hEHHF1cJp3H2OpGpd7oELFGxg==',
    EasePack:
        'sha512-Eauecl1O5GSTGUe/LhI/3bW1ucGUsySCiro8QspjvZa4MRnD7bCPrFckVDvn2v0S/T+l/p7U9LeLELK8xwnf3w==',
    MorphSVGPlugin:
        'sha512-OH0kcFbOHV8V1H3cvIJKSnAd4NGI1RdX5ZzLtvXNxAEPAhL98PddG/d1jgmUXsgoQwILZR3aPtT7Cbbj215P0g==',
    MotionPathPlugin:
        'sha512-vuNgw6lF+p97XmL9lFpbQZWBXxIFnwUVt7J/uknE4NJru5GJjjhIAauXdDtNdezqOJFnit+M3AeaM+gZ93hlmg==',
    Physics2DPlugin:
        'sha512-WiQwJFu9iLtVP465MJtQODD723blEzlcsNycNNIAwUlq5NnyIdVikB3EPf0myrRVynSnroAi8RoNtXmJpYBieQ==',
    ScrambleTextPlugin:
        'sha512-EVIYaMR2tIn6veV5jmP0M0DFBSBt5rAhRPsW0zwC9LyPzAIHNeWiZ4B5orgYeWbNq55vIwk8fOKCtTHwiCBvaw==',
    SplitText:
        'sha512-wOeEC+9qERAzhliwBFPDb6t8TiFFxdxG8vhK/Ygs7TuC44bpg8pg/X2/U/u+0X4fK05wb9id1EIipnF02+CFQw==',
    TextPlugin:
        'sha512-8BkFnMa5pqmphA7XoSbkjP2qX7Gqlkel2C+p1g1AH1tW4cBJs2tumC0Zn9eNRBOKbe3dW9OtbYh/vpkGuZNTMw==',
};

export const COMPOSITION_CSP = [
    "default-src 'none'",
    "script-src 'unsafe-inline' https://cdnjs.cloudflare.com",
    "style-src 'unsafe-inline'",
    'img-src data: blob:',
    'font-src data:',
    'media-src data: blob:',
    "connect-src 'none'",
    "frame-src 'none'",
    "worker-src 'none'",
    "object-src 'none'",
    "base-uri 'none'",
    "form-action 'none'",
].join('; ');

const CLOSING_SCRIPT_TAG = '</' + 'script>';

function nonceAttribute(nonce: string | undefined): string {
    return nonce ? ` nonce="${nonce}"` : '';
}

function scriptTag(id: string, src: string, integrity: string, nonce?: string): string {
    return (
        `<script id="${id}"${nonceAttribute(nonce)} src="${src}" integrity="${integrity}" ` +
        `crossorigin="anonymous">${CLOSING_SCRIPT_TAG}`
    );
}

/**
 * Stamp the host nonce onto the author's own inline `<script>`/`<style>`, which a nonce-based host
 * CSP would otherwise block.
 *
 * Scoped to author markup on purpose. Run over the whole assembled document it also rewrites the
 * inlined snapDOM body, whose minified source contains `<style[^>]*>` regex literals that this
 * pattern matches — leaving snapDOM unable to match a real `<style>` tag. Everything we inject gets
 * its nonce as an attribute at construction instead.
 */
export function applyCspNonce(html: string, nonce: string | undefined): string {
    if (!nonce) {
        return html;
    }
    return html
        .replace(/<script\b(?![^>]*\bnonce=)/gi, `<script nonce="${nonce}"`)
        .replace(/<style\b(?![^>]*\bnonce=)/gi, `<style nonce="${nonce}"`);
}

export const AGENT_SCRIPT_ID = 'l5-agent';
export const SNAPDOM_SCRIPT_ID = 'l5-snapdom';

const AUTHOR_GSAP_SCRIPT = /<script\b[^>]*\bsrc="[^"]*gsap[^"]*"[^>]*>\s*<\/script>/gi;
const EXISTING_MORPH_HELPER = new RegExp(
    `<script id="${MORPH_HELPER_SCRIPT_ID}">[\\s\\S]*?${CLOSING_SCRIPT_TAG}`,
    'i',
);

export function buildCompositionSrcdoc(
    managedHtml: string,
    agentToken?: string,
    nonce?: string,
): string {
    const withoutAuthorGsap = applyCspNonce(
        managedHtml.replace(AUTHOR_GSAP_SCRIPT, '').replace(EXISTING_MORPH_HELPER, ''),
        nonce,
    );
    const attribute = nonceAttribute(nonce);

    const agent = agentToken
        ? `<script id="${SNAPDOM_SCRIPT_ID}"${attribute}>${SNAPDOM_SOURCE}${CLOSING_SCRIPT_TAG}` +
          `<script id="${AGENT_SCRIPT_ID}"${attribute}>` +
          `${compositionAgentSource(agentToken)}${CLOSING_SCRIPT_TAG}`
        : '';

    const bootstrap = [
        '<!doctype html>',
        '<meta charset="utf-8">',
        `<meta http-equiv="Content-Security-Policy" content="${COMPOSITION_CSP}">`,
        agent,
        scriptTag(GSAP_SCRIPT_ID, GSAP_CDN_URL, GSAP_SRI.gsap, nonce),
        ...GSAP_PLUGINS.map((plugin) =>
            scriptTag(gsapPluginScriptId(plugin), gsapPluginUrl(plugin), GSAP_SRI[plugin], nonce),
        ),
        `<script id="${MORPH_HELPER_SCRIPT_ID}"${attribute}>` +
            `${MORPH_HELPER_SOURCE}${CLOSING_SCRIPT_TAG}`,
    ].join('');

    return `${bootstrap}${withoutAuthorGsap}`;
}
